import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Grid, 
  IconButton, 
  Chip,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ptBR from 'date-fns/locale/pt-BR';

import costService from '../services/cost.service';
import { CostType, COST_CATEGORY_LABELS, COST_TYPE_LABELS } from '../types/cost.types';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import AuthLayout from '../components/AuthLayout';
import LoadingIndicator from '../components/LoadingIndicator';
import ErrorFallback from '../components/ErrorFallback';
import EmptyState from '../components/EmptyState';

const CostList: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  
  // Estados para os custos e paginação
  const [costs, setCosts] = useState<CostType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCosts, setTotalCosts] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  
  // Estados para filtros
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState({
    costType: '',
    category: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
    searchTerm: ''
  });
  
  // Estado para diálogo de exclusão
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [costIdToDelete, setCostIdToDelete] = useState<string | null>(null);
  
  // Estado para resumo de custos
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [summary, setSummary] = useState<any>({
    totalAmount: 0,
    totalCount: 0,
    summary: []
  });

  // Carregar custos
  useEffect(() => {
    fetchCosts();
  }, [page, rowsPerPage, filters]);

  const fetchCosts = async () => {
    setLoading(true);
    setError(null);
    console.log('Buscando custos...');
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage
      };
      
      // Adicionar filtros se estiverem definidos
      if (filters.costType) params.costType = filters.costType;
      if (filters.category) params.category = filters.category;
      if (filters.startDate) params.startDate = filters.startDate.toISOString();
      if (filters.endDate) params.endDate = filters.endDate.toISOString();
      if (filters.searchTerm) params.search = filters.searchTerm;
      
      const response = await costService.getCosts(params);
      console.log('Custos recebidos:', response);
      setCosts(response.costs || []);
      setTotalCosts(response.totalCount || 0);
    } catch (error: any) {
      console.error('Erro ao carregar custos:', error);
      setError(error.message || 'Erro ao carregar custos');
      showSnackbar('Erro ao carregar custos. Tente novamente.', 'error');
      // Definir listas vazias para evitar erros de renderização
      setCosts([]);
      setTotalCosts(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchCostSummary = async () => {
    try {
      const params: any = {};
      
      // Adicionar filtros se estiverem definidos
      if (filters.costType) params.costType = filters.costType;
      if (filters.category) params.category = filters.category;
      if (filters.startDate) params.startDate = filters.startDate.toISOString();
      if (filters.endDate) params.endDate = filters.endDate.toISOString();
      if (filters.searchTerm) params.search = filters.searchTerm;
      
      // @ts-ignore - Método getCostSummary não existe, usar getCostsSummary
      const response = await costService.getCostsSummary(params);
      setSummary(response);
      setShowSummary(true);
    } catch (error) {
      console.error('Erro ao carregar resumo de custos:', error);
      showSnackbar('Erro ao carregar resumo de custos. Tente novamente.', 'error');
    }
  };

  // Manipuladores de eventos
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({
      costType: '',
      category: '',
      startDate: null,
      endDate: null,
      searchTerm: ''
    });
    setPage(0);
  };

  const handleOpenDeleteDialog = (costId: string) => {
    setCostIdToDelete(costId);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCostIdToDelete(null);
  };

  const handleDeleteCost = async () => {
    if (!costIdToDelete) return;
    
    try {
      await costService.deleteCost(costIdToDelete);
      showSnackbar('Custo excluído com sucesso!', 'success');
      fetchCosts();
    } catch (error) {
      console.error('Erro ao excluir custo:', error);
      showSnackbar('Erro ao excluir custo. Tente novamente.', 'error');
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // Componentes de renderização
  const renderFilters = () => (
    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo de Custo</InputLabel>
            <Select
              value={filters.costType}
              onChange={(e) => handleFilterChange('costType', e.target.value)}
              label="Tipo de Custo"
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(COST_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Categoria</InputLabel>
            <Select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              label="Categoria"
            >
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(COST_CATEGORY_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DatePicker
              label="Data Inicial"
              value={filters.startDate}
              onChange={(date) => handleFilterChange('startDate', date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DatePicker
              label="Data Final"
              value={filters.endDate}
              onChange={(date) => handleFilterChange('endDate', date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Pesquisar"
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton 
                  size="small" 
                  onClick={() => filters.searchTerm ? handleFilterChange('searchTerm', '') : null}
                >
                  {filters.searchTerm ? <ClearIcon /> : <SearchIcon />}
                </IconButton>
              )
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
            fullWidth
          >
            Limpar Filtros
          </Button>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={fetchCostSummary}
            startIcon={<BarChartIcon />}
            fullWidth
          >
            Ver Resumo
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const renderDeleteDialog = () => (
    <Dialog
      open={deleteDialogOpen}
      onClose={handleCloseDeleteDialog}
    >
      <DialogTitle>Confirmar Exclusão</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Tem certeza que deseja excluir este custo? Esta ação não pode ser desfeita.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDeleteDialog} color="primary">
          Cancelar
        </Button>
        <Button onClick={handleDeleteCost} color="error" autoFocus>
          Excluir
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderSummaryDialog = () => (
    <Dialog
      open={showSummary}
      onClose={() => setShowSummary(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Resumo de Custos</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Total Geral
              </Typography>
              <Typography variant="h4" color="primary">
                {costService.formatCurrency(summary.totalAmount)}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Total de {summary.totalCount} registros
              </Typography>
            </CardContent>
          </Card>
          
          {summary.summary && summary.summary.map((costTypeGroup: any) => (
            <Box key={costTypeGroup.costType} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {COST_TYPE_LABELS[costTypeGroup.costType as keyof typeof COST_TYPE_LABELS]}
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Total: {costService.formatCurrency(costTypeGroup.totalAmount)} ({costTypeGroup.totalCount} registros)
              </Typography>
              
              <Grid container spacing={2}>
                {costTypeGroup.categories.map((category: any) => (
                  <Grid item xs={12} sm={6} md={4} key={category.category}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" color="primary">
                          {COST_CATEGORY_LABELS[category.category]}
                        </Typography>
                        <Typography variant="h6">
                          {costService.formatCurrency(category.totalAmount)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {category.count} registros
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Média: {costService.formatCurrency(category.avgAmount)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <Divider sx={{ my: 2 }} />
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowSummary(false)}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <AuthLayout title="Gestão Financeira - Custos">
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h1">
          Gestão Financeira - Custos
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/costs/new')}
            sx={{ mr: 1 }}
          >
            Novo Custo
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 1 }}
          >
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<BarChartIcon />}
            onClick={() => {
              setShowSummary(true);
              fetchCostSummary();
            }}
          >
            Resumo
          </Button>
        </Box>
      </Box>
      

      
      {showFilters && renderFilters()}
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <LoadingIndicator message="Carregando custos..." />
        ) : error ? (
          <ErrorFallback 
            error={error} 
            resetErrorBoundary={fetchCosts} 
            message="Ocorreu um erro ao carregar os custos." 
          />
        ) : costs.length === 0 ? (
          <EmptyState 
            title="Nenhum custo encontrado" 
            message="Não foram encontrados custos com os filtros selecionados." 
            actionText="Registrar Novo Custo" 
            onAction={() => navigate('/costs/new')} 
          />
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Referência</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {costs.map((cost) => (
                    <TableRow key={cost._id} hover>
                      <TableCell>{cost.description}</TableCell>
                      <TableCell>
                        <Chip 
                          label={COST_TYPE_LABELS[cost.costType as keyof typeof COST_TYPE_LABELS]} 
                          size="small"
                          color={
                            cost.costType === 'technical_direct' ? 'primary' :
                            cost.costType === 'commercial_direct' ? 'secondary' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{COST_CATEGORY_LABELS[cost.category]}</TableCell>
                      <TableCell>{new Date(cost.date).toLocaleDateString()}</TableCell>
                      <TableCell align="right">{costService.formatCurrency(cost.amount)}</TableCell>
                      <TableCell>
                        {cost.serviceRequest && typeof cost.serviceRequest !== 'string' && (
                          <Chip 
                            label={`OS: ${cost.serviceRequest.number}`} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        )}
                        {cost.commercialVisit && typeof cost.commercialVisit !== 'string' && (
                          <Chip 
                            label={`Visita: ${cost.commercialVisit.number}`} 
                            size="small" 
                            variant="outlined"
                            color="secondary"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Visualizar">
                          <IconButton 
                            size="small"
                            onClick={() => navigate(`/costs/${cost._id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton 
                            size="small"
                            onClick={() => navigate(`/costs/${cost._id}/edit`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton 
                            size="small"
                            onClick={() => handleOpenDeleteDialog(cost._id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCosts}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>
      
      {renderDeleteDialog()}
      {renderSummaryDialog()}
    </AuthLayout>
  );
};

export default CostList;
